import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const SERIF_FONT = "'Playfair Display', Georgia, serif";

// Full text logo for light backgrounds (LATECHNOLOGÍA complete)
const FullTextLogo = ({ className = "" }) => (
  <span className={`font-extrabold tracking-tight ${className}`}>
    <span className="text-[#1a1a4e]">LA</span>
    <span className="text-[#22d3ee]">TECHNO</span>
    <span className="text-[#1a1a4e]">LOGÍA</span>
  </span>
);

// Short text logo for gradient backgrounds (LATECH only, no background box)
const ShortTextLogo = ({ className = "" }) => (
  <span className={`font-extrabold tracking-tight ${className}`}>
    <span className="text-[#1a1a4e]">LA</span>
    <span className="text-[#22d3ee]">TECH</span>
  </span>
);

// ============================================
// LOGIN PAGE - Light Theme
// ============================================
export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo - Full LATECHNOLOGÍA for light background */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <FullTextLogo className="text-3xl" />
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <h2 
              className="text-2xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: SERIF_FONT }}
            >
              Iniciar Sesión
            </h2>
            <p className="text-gray-500 mb-6">Accede a tu cuenta para continuar</p>

            {error && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                data-testid="login-submit-btn"
              >
                {loading ? 'Ingresando...' : 'Iniciar Sesión'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                ¿No tienes cuenta?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Regístrate gratis
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Gradient */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-blue-600 to-red-800">
        <div className="h-full flex items-center justify-center p-12">
          <div className="text-white text-center max-w-md">
            {/* LATECH logo - text only, integrates with gradient */}
            <div className="mb-6">
              <span className="text-5xl font-extrabold tracking-tight">
                <span className="text-white">LA</span>
                <span className="text-cyan-300">TECH</span>
              </span>
            </div>
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: SERIF_FONT }}
            >
              El Futuro es Ahora
            </h2>
            <p className="text-blue-100 text-lg">
              Descubre las últimas tendencias en tecnología, innovación y el mundo digital que está transformando todo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// REGISTER PAGE - Light Theme
// ============================================
export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Gradient */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="h-full flex items-center justify-center p-12">
          <div className="text-white text-center max-w-md">
            {/* LATECH logo - text only, integrates with gradient */}
            <div className="mb-6">
              <span className="text-5xl font-extrabold tracking-tight">
                <span className="text-white">LA</span>
                <span className="text-cyan-300">TECH</span>
              </span>
            </div>
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: SERIF_FONT }}
            >
              Únete a la Revolución Digital
            </h2>
            <p className="text-gray-300 text-lg">
              Crea tu cuenta y accede a las últimas noticias sobre tecnología, startups e innovación.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo - Full LATECHNOLOGÍA for light background */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <FullTextLogo className="text-3xl" />
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <h2 
              className="text-2xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: SERIF_FONT }}
            >
              Crear Cuenta
            </h2>
            <p className="text-gray-500 mb-6">Regístrate para acceder a todo el contenido</p>

            {error && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="pl-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pl-10 pr-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                data-testid="register-submit-btn"
              >
                {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Inicia sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// AUTH CALLBACK PAGE
// ============================================
export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCallback } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const errorParam = params.get('error');

      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        return;
      }

      if (token) {
        try {
          await handleCallback(token);
          navigate('/');
        } catch (err) {
          setError('Error processing authentication');
        }
      } else {
        setError('No authentication token received');
      }
    };

    processCallback();
  }, [location, navigate, handleCallback]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center p-8 rounded-2xl bg-gray-50 border border-gray-200">
        {error ? (
          <>
            <p className="text-blue-600 mb-4">{error}</p>
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Volver al inicio de sesión
            </Link>
          </>
        ) : (
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-gray-500">Procesando autenticación...</p>
          </div>
        )}
      </div>
    </div>
  );
};
